// FILE: client/src/App.js
// (This file is updated to include the new routes)

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

// Public Pages and Components
import PublicLayout from './components/public/PublicLayout';
import PublicHomePage from './pages/PublicHomePage';
import SmsConsentPage from './pages/SmsConsentPage';
import AboutPage from './pages/AboutPage';
import PropertiesPage from './pages/PropertiesPage';
import PropertyPage from './pages/PropertyPage';
import SharedCollectionPage from './pages/SharedCollectionPage';
import OfficeRegistryPage from './pages/OfficeRegistryPage';
import ContactPage from './pages/ContactPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WelcomePage from './pages/WelcomePage';

// Secure Portal Pages and Components
import LoginPage from './components/secure/LoginPage';
import PrivateRoute from './components/secure/PrivateRoute';
import PwaUpdateBanner from './components/shared/PwaUpdateBanner';
import BrecAppPage from './pages/BrecAppPage';
import BrecAppAdminPage from './pages/secure/BrecAppAdminPage';
import DashboardLayout from './components/secure/DashboardLayout';
import DashboardOverview from './pages/secure/DashboardOverview';
import AgentListPage from './pages/secure/AgentListPage';
import ClientListPage from './pages/secure/ClientListPage';
import PropertyListPage from './pages/secure/PropertyListPage';
import TransactionListPage from './pages/secure/TransactionListPage';
import ComplianceHubPage from './pages/secure/ComplianceHubPage';
import CompliancePage from './pages/secure/CompliancePage';
import EmployeeListPage from './pages/secure/EmployeeListPage';
import AgentDetailPage from './pages/secure/AgentDetailPage';
import LicenseTrackingPage from './pages/secure/LicenseTrackingPage';
import AgentProductionPage from './pages/secure/AgentProductionPage';
import OperationsDocsPage from './pages/secure/OperationsDocsPage';
import TeamManagementPage from './pages/secure/TeamManagementPage';
import TeamDetailPage from './pages/secure/TeamDetailPage';
import EscrowManagementPage from './pages/secure/EscrowManagementPage';
import AdvertisingReviewPage from './pages/secure/AdvertisingReviewPage';
import VirtualOfficeSettingsPage from './pages/secure/VirtualOfficeSettingsPage';
import IntegrationsPage from './pages/secure/IntegrationsPage';
import EmployeeDetailPage from './pages/secure/EmployeeDetailPage';
import ClientDetailPage from './pages/secure/ClientDetailPage';
import PropertyDetailPage from './pages/secure/PropertyDetailPage';
import TransactionDetailPage from './pages/secure/TransactionDetailPage';
import EscrowAccountDetailPage from './pages/secure/EscrowAccountDetailPage';
import ComplianceDetailPage from './pages/secure/ComplianceDetailPage';
import AdvertisingReviewDetailPage from './pages/secure/AdvertisingReviewDetailPage';
import EscrowLedgerPage from './pages/secure/EscrowLedgerPage';
import FormsPortalPage from './pages/secure/FormsPortalPage'; // <-- ADD THIS LINE
import IndependentContractorAgreementPage from './pages/secure/IndependentContractorAgreementPage'; // <-- ADD THIS LINE
import BuyerAgencyAgreementPage from './pages/secure/BuyerAgencyAgreementPage';
import ExclusiveListingAgreementPage from './pages/secure/ExclusiveListingAgreementPage';
import NoAgencyDisclosurePage from './pages/secure/NoAgencyDisclosurePage';
import TasksPage from './pages/secure/TasksPage';
import MyOfficePage from './pages/secure/MyOfficePage';
import OfficeDirectoryPage from './pages/secure/OfficeDirectoryPage';
import FileCabinetPage from './pages/secure/FileCabinetPage';
import MailboxPage from './pages/secure/MailboxPage';
import PolicyCenterPage from './pages/secure/PolicyCenterPage';
import OnboardingAdminPage from './pages/secure/OnboardingAdminPage';
import FormsCabinetPage from './pages/secure/FormsCabinetPage';
import AccountingPage from './pages/secure/AccountingPage';
import EscrowCompliancePage from './pages/secure/EscrowCompliancePage';
import CommissionsPage from './pages/secure/CommissionsPage';
import PayrollPage from './pages/secure/PayrollPage';
import ChecksPage from './pages/secure/ChecksPage';
import UserAccountsPage from './pages/secure/UserAccountsPage';
import JobAccessPage from './pages/secure/JobAccessPage';
import CorporateRecordsPage from './pages/secure/CorporateRecordsPage';
import HRCompliancePage from './pages/secure/HRCompliancePage';
import CommandCenterPage from './pages/secure/CommandCenterPage';
import SystemStatusPage from './pages/secure/SystemStatusPage';
import TerritoriesPage from './pages/secure/TerritoriesPage';
import LeadRoutingPage from './pages/secure/LeadRoutingPage';
import MyPlanPage from './pages/secure/MyPlanPage';
import TaskAutomationPage from './pages/secure/TaskAutomationPage';
import MyOnboardingPage from './pages/secure/MyOnboardingPage';
import PropertyManagementPage from './pages/secure/PropertyManagementPage';
import PMPortfolioPage from './pages/secure/PMPortfolioPage';
import PMOwnersPage from './pages/secure/PMOwnersPage';
import PMLeasingPage from './pages/secure/PMLeasingPage';
import PMDeadlinesPage from './pages/secure/PMDeadlinesPage';
import PMAuthorityPage from './pages/secure/PMAuthorityPage';
import PMTrustAccountsPage from './pages/secure/PMTrustAccountsPage';
import PMMonthEndPage from './pages/secure/PMMonthEndPage';
import PMOnboardingPage from './pages/secure/PMOnboardingPage';
import PMMaintenancePage from './pages/secure/PMMaintenancePage';
import PMCommercialPage from './pages/secure/PMCommercialPage';
import PMReportsPage from './pages/secure/PMReportsPage';
import CorporateBookPage from './pages/secure/CorporateBookPage';
import ExpensesPage from './pages/secure/ExpensesPage';
import MyCommissionsPage from './pages/secure/MyCommissionsPage';
import DocumentLibraryPage from './pages/secure/DocumentLibraryPage';
import RetentionPage from './pages/secure/RetentionPage';
import ZoningRolodex from './components/secure/ZoningRolodex';
import MySiteSearchesPage from './pages/secure/MySiteSearchesPage';
import SiteSearchClipPage from './pages/secure/SiteSearchClipPage';
import SiteSearchAlertsPage from './pages/secure/SiteSearchAlertsPage';
import DealCompliancePage from './pages/secure/DealCompliancePage';
import ChangePasswordPage from './pages/secure/ChangePasswordPage';
import SecurityPage, { TwoFactorEnrollPage } from './pages/secure/SecurityPage';
import PhoneNotificationsPage from './pages/secure/PhoneNotificationsPage';
import PasswordFreePage from './pages/secure/PasswordFreePage';
import TaxCenterPage from './pages/secure/TaxCenterPage';
import ContactCompliancePage from './pages/secure/ContactCompliancePage';
import SocialMediaPage from './pages/secure/SocialMediaPage';
import CampaignsPage from './pages/secure/CampaignsPage';
import ExaminerAccessPage from './pages/secure/ExaminerAccessPage';
import ExaminerMyAccessPage from './pages/secure/ExaminerMyAccessPage';
import OfficeRegistryInternalPage from './pages/secure/OfficeRegistryInternalPage';
import IntranetPage from './pages/secure/IntranetPage';
import CrmPage from './pages/secure/CrmPage';
import CrmContactPage from './pages/secure/CrmContactPage';
import HotLeadsPage from './pages/secure/HotLeadsPage';
import PropertySearchPage from './pages/secure/PropertySearchPage';
import PropertyFlyerPage from './pages/secure/PropertyFlyerPage';
import WebsiteChatPage from './pages/secure/WebsiteChatPage';
import MyWebsiteLeadsPage from './pages/secure/MyWebsiteLeadsPage';
import PortalAssistantPage from './pages/secure/PortalAssistantPage';
import { wave2DashboardRoutes, wave2PublicRoutes, wave2StandaloneRoutes } from './wave2Routes';
import { deskDashboardRoutes } from './deskRoutes';
import { trainingDashboardRoutes, trainingStandaloneRoutes } from './trainingRoutes';
import { ownerPortalRoutes, ownerStandaloneRoutes, ownerDashboardRoutes } from './ownerListingRoutes';
import { seoPublicRoutes, seoDashboardRoutes } from './seoRoutes';
import { recruitingPublicRoutes, recruitingDashboardRoutes } from './recruitingRoutes';
import { showingPublicRoutes, showingDashboardRoutes } from './showingRoutes';
import { cmaDashboardRoutes } from './cmaRoutes';

// Client Portal Pages and Components (fully separate auth system — see hooks/useClientPortalAuth.js)
import PortalPrivateRoute from './components/clientPortal/PortalPrivateRoute';
import PortalLayout from './components/clientPortal/PortalLayout';
import PortalLoginPage from './pages/clientPortal/PortalLoginPage';
import FormSignPage from './pages/FormSignPage';
import PortalChangePasswordPage from './pages/clientPortal/PortalChangePasswordPage';
import PortalDashboardPage from './pages/clientPortal/PortalDashboardPage';
import PortalSiteSearchPage from './pages/clientPortal/PortalSiteSearchPage';
import PortalTransactionPage from './pages/clientPortal/PortalTransactionPage';
import PortalRegisterPage from './pages/clientPortal/PortalRegisterPage';
import PortalMyPropertiesPage from './pages/clientPortal/PortalMyPropertiesPage';

// Property Management Portal — owners and tenants. A third auth system, separate again
// from both staff and the client portal (see hooks/usePmPortalAuth.js).
import PmPortalPrivateRoute from './components/pmPortal/PmPortalPrivateRoute';
import PmPortalLayout from './components/pmPortal/PmPortalLayout';
import PmPortalLoginPage from './pages/pmPortal/PmPortalLoginPage';
import PmPortalChangePasswordPage from './pages/pmPortal/PmPortalChangePasswordPage';
import OwnerOverviewPage from './pages/pmPortal/OwnerOverviewPage';
import OwnerStatementPage from './pages/pmPortal/OwnerStatementPage';
import OwnerBudgetPage from './pages/pmPortal/OwnerBudgetPage';
import OwnerProjectionsPage from './pages/pmPortal/OwnerProjectionsPage';
import OwnerDistributionsPage from './pages/pmPortal/OwnerDistributionsPage';
import OwnerMaintenancePage from './pages/pmPortal/OwnerMaintenancePage';
import TenantOverviewPage from './pages/pmPortal/TenantOverviewPage';
import TenantLeasePage from './pages/pmPortal/TenantLeasePage';
import TenantLedgerPage from './pages/pmPortal/TenantLedgerPage';
import TenantMaintenancePage from './pages/pmPortal/TenantMaintenancePage';
import TenantNoticesPage from './pages/pmPortal/TenantNoticesPage';
import TenantPayPage from './pages/pmPortal/TenantPayPage';
import PMOnlinePaymentsPage from './pages/secure/PMOnlinePaymentsPage';
import PortalDocumentsPage from './pages/pmPortal/PortalDocumentsPage';
import PmPortalHome from './pages/pmPortal/PmPortalHome';


// Remount the property page per listing so its gallery and form start fresh.
const PropertiesSlug = () => { const { slug } = useParams(); return <PropertyPage key={slug} />; };

function App() {
    const { token, user, login, logout, error, loading,
            challenge, verifySecondFactor, cancelSecondFactor, adoptPasswordlessSession } = useAuth();
    
    return (
        <Router>
            <Routes>
                {/* Public-facing routes */}
                <Route path="/" element={<PublicLayout />}>
                    <Route index element={<PublicHomePage />} />
                    <Route path="about" element={<AboutPage />} />
                    <Route path="properties" element={<PropertiesPage />} />
                    <Route path="properties/:slug" element={<PropertiesSlug />} />
                    <Route path="shared/:token" element={<SharedCollectionPage />} />
                    {/* "Listings" became "Properties"; the server also answers /listings with a 301. */}
                    <Route path="listings" element={<Navigate to="/properties" replace />} />
                    <Route path="office-registry" element={<OfficeRegistryPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="sms-consent" element={<SmsConsentPage />} />
                    {/* The BREC app: how to install it on each device (the link the office hands out). */}
                    <Route path="app" element={<BrecAppPage />} />
                    {wave2PublicRoutes()}
                    {seoPublicRoutes()}
                    {recruitingPublicRoutes()}
                    {showingPublicRoutes()}
                </Route>
                {wave2StandaloneRoutes()}
                {trainingStandaloneRoutes()}
                {ownerStandaloneRoutes()}

                {/* Secure portal login */}
                <Route
                    path="/portal"
                    element={token ? <Navigate to="/dashboard" /> : <LoginPage onLogin={login} error={error} loading={loading}
                        challenge={challenge} onVerify={verifySecondFactor} onCancelVerify={cancelSecondFactor}
                        onSession={adoptPasswordlessSession} />}
                />

                {/* Public self-service password recovery */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {/* An agent's invite link: choose a password, then install the app (routes/agentInvites.js). */}
                <Route path="/welcome" element={<WelcomePage />} />
                
                {/* Forced password change: a user on a temporary password can't reach
                    the rest of the portal until they set their own password. */}
                <Route
                    path="/dashboard"
                    element={
                        user?.mustChangePassword
                            ? <PrivateRoute><ChangePasswordPage user={user} forced /></PrivateRoute>
                            : user?.twoFactorEnrollmentRequired
                                // The firm requires two-factor for this login and it isn't set
                                // up: nothing else until it is (the server enforces this too).
                                ? <PrivateRoute><TwoFactorEnrollPage user={user} onLogout={logout} /></PrivateRoute>
                                : <PrivateRoute><DashboardLayout user={user} onLogout={logout} /></PrivateRoute>
                    }
                >
                    <Route index element={<DashboardOverview />} />
                    <Route path="my-office" element={<MyOfficePage />} />
                    <Route path="app" element={<BrecAppAdminPage />} />
                    <Route path="office-directory" element={<OfficeDirectoryPage />} />
                    <Route path="file-cabinet" element={<FileCabinetPage />} />
                    <Route path="mailroom" element={<MailboxPage />} />
                    <Route path="policy-center" element={<PolicyCenterPage />} />
                    <Route path="onboarding" element={<OnboardingAdminPage user={user} />} />
                    <Route path="forms-cabinet" element={<FormsCabinetPage />} />
                    <Route path="accounting" element={<AccountingPage />} />
                    <Route path="escrow-compliance" element={<EscrowCompliancePage />} />
                    <Route path="commissions" element={<CommissionsPage />} />
                    <Route path="payroll" element={<PayrollPage user={user} />} />
                    <Route path="checks" element={<ChecksPage user={user} />} />
                    <Route path="user-accounts" element={<UserAccountsPage />} />
                    <Route path="job-access" element={<JobAccessPage />} />
                    <Route path="corporate-records" element={<CorporateRecordsPage />} />
                    <Route path="hr" element={<HRCompliancePage />} />
                    <Route path="command-center" element={<CommandCenterPage />} />
                    <Route path="system-status" element={<SystemStatusPage />} />
                    <Route path="territories" element={<TerritoriesPage />} />
                    <Route path="lead-routing" element={<LeadRoutingPage />} />
                    <Route path="my-plan" element={<MyPlanPage />} />
                    <Route path="task-automation" element={<TaskAutomationPage />} />
                    <Route path="my-onboarding" element={<MyOnboardingPage />} />
                    <Route path="corporate-book" element={<CorporateBookPage />} />
                    <Route path="property-management" element={<PropertyManagementPage />} />
                    <Route path="pm-portfolio" element={<PMPortfolioPage />} />
                    <Route path="pm-owners" element={<PMOwnersPage />} />
                    <Route path="pm-leasing" element={<PMLeasingPage />} />
                    <Route path="pm-deadlines" element={<PMDeadlinesPage />} />
                    <Route path="pm-authority" element={<PMAuthorityPage />} />
                    <Route path="pm-trust-accounts" element={<PMTrustAccountsPage />} />
                    <Route path="pm-month-end" element={<PMMonthEndPage />} />
                    <Route path="pm-online-payments" element={<PMOnlinePaymentsPage />} />
                    <Route path="pm-onboarding" element={<PMOnboardingPage />} />
                    <Route path="pm-maintenance" element={<PMMaintenancePage />} />
                    <Route path="pm-commercial" element={<PMCommercialPage />} />
                    <Route path="pm-reports" element={<PMReportsPage />} />
                    <Route path="expenses" element={<ExpensesPage />} />
                    <Route path="my-commissions" element={<MyCommissionsPage />} />
                    <Route path="document-library" element={<DocumentLibraryPage />} />
                    <Route path="retention" element={<RetentionPage />} />
                    <Route path="zoning-library" element={<ZoningRolodex />} />
                    <Route path="my-site-searches" element={<MySiteSearchesPage />} />
                    <Route path="site-search-clip" element={<SiteSearchClipPage />} />
                    <Route path="site-search-alerts" element={<SiteSearchAlertsPage />} />
                    <Route path="deal-compliance" element={<DealCompliancePage />} />
                    <Route path="agents" element={<AgentListPage />} />
                    <Route path="agents/:id" element={<AgentDetailPage />} />
                    <Route path="teams" element={<TeamManagementPage />} />
                    <Route path="teams/:id" element={<TeamDetailPage />} />
                    <Route path="employees" element={<EmployeeListPage />} />
                    <Route path="employees/:id" element={<EmployeeDetailPage />} />
                    <Route path="license-tracking" element={<LicenseTrackingPage />} />
                    <Route path="agent-production" element={<AgentProductionPage />} />
                    <Route path="clients" element={<ClientListPage />} />
                    <Route path="clients/:id" element={<ClientDetailPage />} />
                    <Route path="crm" element={<CrmPage />} />
                    <Route path="crm/contacts/:id" element={<CrmContactPage />} />
                    <Route path="hot-leads" element={<HotLeadsPage />} />
                    <Route path="website-chat" element={<WebsiteChatPage />} />
                    <Route path="my-website-leads" element={<MyWebsiteLeadsPage />} />
                    <Route path="assistant" element={<PortalAssistantPage />} />
                    <Route path="properties" element={<PropertyListPage />} />
                    <Route path="properties/:id" element={<PropertyDetailPage />} />
                    <Route path="property-search" element={<PropertySearchPage />} />
                    <Route path="transactions" element={<TransactionListPage />} />
                    <Route path="transactions/:id" element={<TransactionDetailPage />} />
                    <Route path="compliance" element={<CompliancePage />} />
                    <Route path="compliance/checklist" element={<ComplianceHubPage />} />
                    <Route path="compliance/:id" element={<ComplianceDetailPage />} />
                    <Route path="escrow" element={<EscrowManagementPage />} />
                    <Route path="escrow/:id" element={<EscrowAccountDetailPage />} />
                    <Route path="escrow/ledgers/:ledgerId" element={<EscrowLedgerPage />} />
                    <Route path="advertising" element={<AdvertisingReviewPage />} />
                    <Route path="advertising/:id" element={<AdvertisingReviewDetailPage />} />
                    <Route path="virtual-office-settings" element={<VirtualOfficeSettingsPage />} />
                    <Route path="integrations" element={<IntegrationsPage />} />
                    <Route path="operations" element={<OperationsDocsPage />} />
                    <Route path="forms" element={<FormsPortalPage />} /> {/* <-- ADD THIS LINE */}
                    <Route path="forms/independent-contractor-agreement" element={<IndependentContractorAgreementPage />} /> {/* <-- ADD THIS LINE */}
                    <Route path="forms/buyer-agency" element={<BuyerAgencyAgreementPage />} />
                    <Route path="forms/listing-agreement" element={<ExclusiveListingAgreementPage />} />
                    <Route path="forms/no-agency" element={<NoAgencyDisclosurePage />} />
                    <Route path="tasks" element={<TasksPage />} />
                    <Route path="change-password" element={<ChangePasswordPage user={user} />} />
                    <Route path="security" element={<SecurityPage user={user} />} />
                    <Route path="passwordless" element={<PasswordFreePage user={user} />} />
                    <Route path="notifications" element={<PhoneNotificationsPage user={user} />} />
                    <Route path="tax-center" element={<TaxCenterPage user={user} />} />
                    <Route path="contact-compliance" element={<ContactCompliancePage />} />
                    <Route path="social-media" element={<SocialMediaPage />} />
                    <Route path="campaigns" element={<CampaignsPage />} />
                    <Route path="examiner-access" element={<ExaminerAccessPage />} />
                    <Route path="my-access" element={<ExaminerMyAccessPage />} />
                    <Route path="office-registry" element={<OfficeRegistryInternalPage />} />
                    <Route path="intranet" element={<IntranetPage user={user} />} />
                    {wave2DashboardRoutes()}
                    {deskDashboardRoutes()}
                    {showingDashboardRoutes()}
                    {cmaDashboardRoutes()}
                    {trainingDashboardRoutes()}
                    {ownerDashboardRoutes()}
                    {seoDashboardRoutes()}
                    {recruitingDashboardRoutes()}
                </Route>

                {/* Print flyer for one of our listings: staff login, but outside the dashboard
                    frame so only the flyer prints. */}
                <Route path="/flyer/:id" element={<PrivateRoute><PropertyFlyerPage /></PrivateRoute>} />

                {/* Client Portal — entirely separate route tree, not nested under /dashboard or PrivateRoute */}
                {/* Signing by link — public by design: the token is the authority. */}
                <Route path="/forms/sign/:token" element={<FormSignPage />} />

                <Route path="/client-portal/login" element={<PortalLoginPage />} />
                <Route path="/client-portal/register" element={<PortalRegisterPage />} />
                <Route
                    path="/client-portal/change-password"
                    element={<PortalPrivateRoute><PortalChangePasswordPage /></PortalPrivateRoute>}
                />
                <Route
                    path="/client-portal"
                    element={<PortalPrivateRoute><PortalLayout /></PortalPrivateRoute>}
                >
                    <Route path="dashboard" element={<PortalDashboardPage />} />
                    <Route path="property-search" element={<PortalSiteSearchPage />} />
                    <Route path="my-properties" element={<PortalMyPropertiesPage />} />
                    <Route path="transactions/:id" element={<PortalTransactionPage />} />
                    {ownerPortalRoutes()}
                </Route>

                {/* Property Management Portal — a sibling of /client-portal, never nested
                    under PrivateRoute. Owners and tenants share a login and a layout; the
                    role in the session decides which tree they land in. */}
                <Route path="/pm-portal/login" element={<PmPortalLoginPage />} />
                <Route
                    path="/pm-portal/change-password"
                    element={<PmPortalPrivateRoute><PmPortalChangePasswordPage /></PmPortalPrivateRoute>}
                />
                <Route
                    path="/pm-portal"
                    element={<PmPortalPrivateRoute><PmPortalLayout /></PmPortalPrivateRoute>}
                >
                    <Route index element={<PmPortalHome />} />
                    <Route path="documents" element={<PortalDocumentsPage />} />

                    <Route path="owner" element={<PmPortalPrivateRoute requireRole="owner"><Outlet /></PmPortalPrivateRoute>}>
                        <Route index element={<OwnerOverviewPage />} />
                        <Route path="statement" element={<OwnerStatementPage />} />
                        <Route path="budget" element={<OwnerBudgetPage />} />
                        <Route path="projections" element={<OwnerProjectionsPage />} />
                        <Route path="distributions" element={<OwnerDistributionsPage />} />
                        <Route path="maintenance" element={<OwnerMaintenancePage />} />
                    </Route>

                    <Route path="tenant" element={<PmPortalPrivateRoute requireRole="tenant"><Outlet /></PmPortalPrivateRoute>}>
                        <Route index element={<TenantOverviewPage />} />
                        <Route path="lease" element={<TenantLeasePage />} />
                        <Route path="ledger" element={<TenantLedgerPage />} />
                        <Route path="pay" element={<TenantPayPage />} />
                        <Route path="maintenance" element={<TenantMaintenancePage />} />
                        <Route path="notices" element={<TenantNoticesPage />} />
                    </Route>
                </Route>
            </Routes>
            <PwaUpdateBanner />
        </Router>
    );
}

export default App;
